/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { toUpper, memoize, compact } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import { Column, ColumnInstance, Row } from 'react-table';
import { MaterialIcon, Text, Tag, BrandColors } from '@blend/enterprise-components';
import { BlendBridge } from 'blb-types/blendBridge';
import { convertToDateFormatUTC, getDiffInUnits } from 'blb-utils/date';
import { closingTypeToDisplay } from 'blb-constants/global';
import { titleCase } from 'blb-utils/format';

const styles = StyleSheet.create({
  tableHeaderContainer: {
    alignItems: 'center',
    display: 'flex',
  },
});

const TableHeader: React.FC<{ column: ColumnInstance<BlendBridge.API.Loan> }> = ({ column }) => {
  const unsortedIconType = 'unfold_more';
  const sortedIconType = column.isSortedDesc ? 'arrow_downward' : 'arrow_upward';
  const iconType = column.isSorted ? sortedIconType : unsortedIconType;

  return (
    <div className={css(styles.tableHeaderContainer)}>
      <Text color={BrandColors.NIGHT} fontSize="sm" weight="semibold">
        {toUpper(column.id)}
      </Text>
      <MaterialIcon color={BrandColors.NIGHT} size={16} type={iconType} />
    </div>
  );
};

const TableCell: React.FC<{ value: string }> = ({ value }) => {
  return <Text>{value}</Text>;
};

const TasksCell: React.FC<{ value: number }> = ({ value }) => {
  const pluralSuffix = value && value > 1 ? 's' : '';
  const cellText = toUpper(`${value} TASK${pluralSuffix}`);
  return value === 0 ? null : <Tag text={cellText} theme="warning" />;
};

/**
 * @param isoString - ISO formatted date string
 * @returns true if an isoString is undefined or in the past
 */
const shouldBeLast = (isoString: string) => {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  return getDiffInUnits(isoString, todayStart.toISOString(), 'days') < 0;
};

/**
 * Docs for sortType: https://react-table.tanstack.com/docs/api/useSortBy#column-options
 * NOTE: Dates before today are placed last, regardless of sort order.
 *
 * DESC: closest dates to today are first (so "closest" is equivalent to "largest")
 * ASC:  furthest dates from today are first ("furthest" is equivalient to "smallest")
 *
 * @returns If the date in rowA is closest to today return -1.
 *          If the date in rowB is furthest from today return 1.
 *          If a row contains a date in the past or has no date, it is set to 1 if ASC and -1 if DESC.
 *          In the case of no date vs. a date in the past, the date in the past should be set below the empty record.
 */
const sortByClosingDate = (
  rowA: Row<BlendBridge.API.Loan>,
  rowB: Row<BlendBridge.API.Loan>,
  colId: string,
  desc?: boolean,
) => {
  const isoA = rowA.original.closing?.closingEnd!;
  const isoB = rowB.original.closing?.closingEnd!;

  const isALast = shouldBeLast(isoA);
  const isBLast = shouldBeLast(isoB);

  const setALast = desc ? -1 : 1; // return value to force rowA to be at the bottom of the list
  const setBLast = desc ? 1 : -1; // return value to force rowB to be at the bottom of the list

  if (isALast) {
    return setALast;
  }
  if (isBLast) {
    return setBLast;
  }
  // NOTE: the below conditions CANNOT be combined with the above conditions even though it looks tempting.
  // This is because of the case where rowA is undefined and rowB has a date in the past.
  // In such a case, rowB should be at the bottom of the list and rowA should be near the end but above rowB.
  if (!isoA) {
    return setALast;
  }
  if (!isoB) {
    return setBLast;
  }

  const daysApart = getDiffInUnits(isoA, isoB, 'days');
  return daysApart >= 0 ? -1 : 1;
};

// sortByClosingDate must be memoized according to the
// sortType docs https://react-table.tanstack.com/docs/api/useSortBy#column-options
const memoizedSort = memoize(
  sortByClosingDate,
  (rowA: Row<BlendBridge.API.Loan>, rowB: Row<BlendBridge.API.Loan>, colId: string, desc?: boolean) =>
    `${rowA.id}_${rowB.id}_${desc ? 'DESC' : 'ASC'}`,
);

interface BaseColumnConfig {
  Cell: React.FC<{ value: string }>;
  Header: React.FC<{ column: ColumnInstance<BlendBridge.API.Loan> }>;
  sortDescFirst: boolean;
}

const baseColumnConfig: BaseColumnConfig = {
  Cell: TableCell,
  Header: TableHeader,
  sortDescFirst: true,
};

const baseColumnConfigurations: Column<BlendBridge.API.Loan>[] = [
  {
    ...baseColumnConfig,
    accessor: loan => titleCase(loan.primaryBorrowerName),
    id: 'Borrower Name',
    width: '18%',
  },
  {
    ...baseColumnConfig,
    accessor: loan => compact([loan.propertyAddress?.lineText, loan.propertyAddress?.cityName]).join(', '),
    id: 'Property Address',
    width: '30%',
  },
  {
    ...baseColumnConfig,
    accessor: loan => loan.propertyAddress?.stateCode,
    id: 'State',
    width: '8%',
  },
  {
    ...baseColumnConfig,
    accessor: loan => titleCase(loan.lender),
    id: 'Lender',
    width: '12%',
  },
];

const CLOSING_DATE_COLUMN: Column<BlendBridge.API.Loan> = {
  ...baseColumnConfig,
  accessor: ({ closing }) => closing && closing.closingStart && convertToDateFormatUTC(closing.closingStart),
  id: 'Closing Date',
  sortType: memoizedSort,
  width: '14%',
};

const LOAN_TYPE_COLUMN: Column<BlendBridge.API.Loan> = {
  ...baseColumnConfig,
  accessor: loan => {
    if (loan.loanType === 'MORTGAGE' && loan.loanPurpose) {
      return titleCase(loan.loanPurpose);
    }
    return titleCase(loan.loanType);
  },
  id: 'Loan Type',
  width: '10%',
};

const OPEN_TASKS_COLUMN: Column<BlendBridge.API.Loan> = {
  ...baseColumnConfig,
  accessor: loan => loan.numberOfTasks,
  Cell: TasksCell,
  id: 'Tasks',
  width: '8%',
};

const opsDeskAgentColumnConfigurations: Column<BlendBridge.API.Loan>[] = [
  ...baseColumnConfigurations,
  {
    ...baseColumnConfig,
    accessor: ({ closing }) => closing && closingTypeToDisplay[closing.closingType],
    id: 'Closing Type',
    width: '10%',
  },
  CLOSING_DATE_COLUMN,
  OPEN_TASKS_COLUMN,
];

const settlementAgentColumnConfigurations: Column<BlendBridge.API.Loan>[] = [
  ...baseColumnConfigurations,
  LOAN_TYPE_COLUMN,
  CLOSING_DATE_COLUMN,
  OPEN_TASKS_COLUMN,
];

const attorneyColumnConfigurations: Column<BlendBridge.API.Loan>[] = [
  ...baseColumnConfigurations,
  LOAN_TYPE_COLUMN,
  CLOSING_DATE_COLUMN,
];

const notaryColumnConfigurations: Column<BlendBridge.API.Loan>[] = [
  ...baseColumnConfigurations,
  {
    ...baseColumnConfig,
    accessor: loan => {
      if (loan.loanType === 'MORTGAGE' && loan.loanPurpose) {
        return titleCase(loan.loanPurpose);
      }
      return loan.loanType;
    },
    id: 'Loan Type',
    width: '10%',
  },
  CLOSING_DATE_COLUMN,
];

const titleAgentColumnConfigurations: Column<BlendBridge.API.Loan>[] = [
  ...baseColumnConfigurations,
  LOAN_TYPE_COLUMN,
  CLOSING_DATE_COLUMN,
];

const loanProcessorColumnConfigurations: Column<BlendBridge.API.Loan>[] = [
  ...baseColumnConfigurations,
  {
    ...baseColumnConfig,
    accessor: ({ closing }) => closing && closingTypeToDisplay[closing.closingType],
    id: 'Closing Type',
    width: '10%',
  },
  CLOSING_DATE_COLUMN,
];

export type PipelinePrincipalType = Exclude<BlendBridge.PrincipalType, 'multi_tenant_tpr' | 'insurance_qc_agent'>;

export const getColumnConfigurations = (principalType: PipelinePrincipalType): Column<BlendBridge.API.Loan>[] => {
  const configs: Record<PipelinePrincipalType, Column<BlendBridge.API.Loan>[]> = {
    attorney: attorneyColumnConfigurations,
    loan_processor: loanProcessorColumnConfigurations,
    notary: notaryColumnConfigurations,
    ops_desk_agent: opsDeskAgentColumnConfigurations,
    settlement_agent: settlementAgentColumnConfigurations,
    title_agent: titleAgentColumnConfigurations,
  };
  return configs[principalType];
};
